import React, { useState, useEffect, useContext } from "react";
import "./externalformtable.css";
import { SelectDropdown } from "../../../HecoForms/Utils/SelectDropdown";
import { DateCalendar } from "../../../HecoForms/Utils/DateCalendar";
import AutocompleteNotes from "../../../HecoForms/Utils/AutocompleNotes";
import { DataRequestContext } from "../../../context/DataRequestContext";
import { Tooltip, ThemeProvider } from "@mui/material";
import { tooltipsTheme } from "../../../HecoForms/CustomThemes/ToolTipThemes";

/**
 * Table that displays the data from the JSON file
 */

export default function ExternalFormTable({
  propertiesData,
  showCheckboxes,
  checkboxState,
  setCheckboxState,
}) {
  const { formData, setFormData } = useContext(DataRequestContext);
  const [data, setData] = useState(propertiesData);

  useEffect(() => {
    setData(propertiesData);
  }, [propertiesData]);

  const updateLocalStorage = (updatedData) => {
    const updatedFormData = {
      ...formData,
      FileData: { ...formData.FileData, PropertiesData: updatedData },
    };
    setFormData(updatedFormData);
    localStorage.setItem("DataRequest", JSON.stringify(updatedFormData));
  };

  const handlePropertyChange = (propertyType, newValue, property) => {
    const updatedData = data.map((item) => {
      return item.Property === property
        ? { ...item, [propertyType]: newValue }
        : item;
    });
    setData(updatedData);
    updateLocalStorage(updatedData);
  };

  const handleValueChange = (newValue, property) => {
    handlePropertyChange("Value (per property)", newValue, property);
  };

  const handleQualityChange = (newQuality, property) => {
    handlePropertyChange("Quality (per value)", newQuality, property);
  };

  const handleNotesChange = (newNotes, property) => {
    handlePropertyChange("Notes (per property)", newNotes, property);
  };

  const renderValueInput = (property) => {
    const numericValue = parseFloat(
      property["Value (per property)"]
        ? property["Value (per property)"]
            .toString()
            .replace(/,/g, "")
            .replace(/[£]/g, "")
        : ""
    );

    if (
      property["Value Format"] === "Free text" ||
      property["Value Format"] === "Text"
    ) {
      return (
        <div className="textarea-input-wrapper">
          <textarea
            className="formrow-value-textareainput"
            placeholder="Write your text..."
            value={property["Value (per property)"]}
            onChange={(e) =>
              handleValueChange(e.target.value, property.Property)
            }
            rows={1}
          />
        </div>
      );
    } else if (property["Value Format"] === "Date format") {
      return (
        <DateCalendar
          values={property["Value (per property)"]}
          onValueChange={(newValue) =>
            handleValueChange(newValue, property.Property)
          }
        />
      );
    } else if (
      property["Value Format"] === "Free text (number format)" ||
      property["Value Format"] === "Number"
    ) {
      return (
        <input
          className="formrow-value-numberinput"
          type="number"
          placeholder="Choose a number value..."
          value={numericValue}
          onChange={(e) => handleValueChange(e.target.value, property.Property)}
        />
      );
    } else if (property["Value Format"] === "Long Value") {
      return (
        <textarea
          className="formrow-value-textareainput"
          placeholder="Write your value..."
          value={property["Value (per property)"]}
          onChange={(e) => handleValueChange(e.target.value, property.Property)}
        />
      );
    } else if (property["Value Format"] === "From drop down") {
      return (
        <SelectDropdown
          values={property["Value (per property)"]}
          valueOptions={property["ValueOptions (per property)"]}
          onValueChange={(newValue) =>
            handleValueChange(newValue, property.Property)
          }
        />
      );
    }
    return (
      <input
        type="text"
        className="formrow-value-numberinput"
        defaultValue={property["Value (per property)"]}
      />
    );
  };

  const renderQualityInput = (property) => {
    return (
      <SelectDropdown
        values={property["Quality (per value)"]}
        valueOptions={property["QualityOptions (per value)"]}
        onValueChange={(newQuality) =>
          handleQualityChange(newQuality, property.Property)
        }
      />
    );
  };

  const renderNotesInput = (property) => {
    return (
      <AutocompleteNotes
        note={property["Notes (per property)"]}
        onNotesChange={(newNotes) =>
          handleNotesChange(newNotes, property.Property)
        }
      />
    );
  };

  const toggleCheckbox = (index) => {
    setCheckboxState((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const toggleAllCheckboxes = () => {
    const newState = {};
    data.forEach((_, index) => {
      newState[index] = !checkboxState.all;
    });
    setCheckboxState({ ...newState, all: !checkboxState.all });
  };

  return (
    <div className="externalform-table-container">
      <table id="externalform-table">
        <thead>
          <tr>
            <th>Property</th>
            <th>Value</th>
            <th>Quality</th>
            <th>Notes</th>
            {showCheckboxes && (
              <th>
                <input
                  type="checkbox"
                  checked={checkboxState.all || false}
                  onChange={toggleAllCheckboxes}
                  className={`externalform-table-checkbox ${
                    checkboxState.all ? "green-tick" : "grey-tick"
                  }`}
                />
              </th>
            )}
          </tr>
        </thead>
        <tbody className="externalform-body">
          {data.map((property, index) => (
            <tr key={index}>
              <td>
                <ThemeProvider theme={tooltipsTheme}>
                  <Tooltip
                    title={property["Property Tooltip"]}
                    className="property-tooltip-box"
                  >
                    <div>{property.Property}</div>
                  </Tooltip>
                </ThemeProvider>
              </td>
              <td>{renderValueInput(property)}</td>
              <td>{renderQualityInput(property)}</td>
              <td>{renderNotesInput(property)}</td>
              {showCheckboxes && (
                <td>
                  <input
                    type="checkbox"
                    checked={checkboxState[index] || property.isMerged || false}
                    onChange={() => toggleCheckbox(index)}
                    className={`externalform-table-checkbox ${
                      checkboxState[index] || property.isMerged
                        ? "green-tick"
                        : "grey-tick"
                    }`}
                  />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
